/**
 * ProfileEditor Component
 * Allows users (patients and veterinarians) to view and edit their profile information
 */

import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Building2, Save, X, Edit2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PatientProfileEditor = ({ token, onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patients/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditForm(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Erreur lors du chargement du profil');
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/patients/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          phone: editForm.phone,
          address: editForm.address,
          city: editForm.city,
          postal_code: editForm.postal_code
        })
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setIsEditing(false);
        toast.success('Profil mis à jour avec succès');
        if (onUpdate) onUpdate(updatedProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erreur de connexion');
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-8 text-gray-500">
        <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Profil non trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <User className="w-5 h-5 text-green-600" />
          Mon Profil
        </h2>
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            size="sm"
            data-testid="edit-profile-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setIsEditing(false);
                setEditForm(profile);
              }}
              variant="outline"
              size="sm"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
              className="bg-green-500 hover:bg-green-600"
              data-testid="save-profile-btn"
            >
              {isSaving ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <User className="w-4 h-4" />
            Nom complet
          </Label>
          {isEditing ? (
            <Input
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              placeholder="Votre nom"
              data-testid="profile-name-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.name || '-'}</p>
          )}
        </div>

        {/* Email (non-editable) */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </Label>
          <p className="text-gray-800 font-medium py-2">{profile.email || '-'}</p>
          {isEditing && (
            <p className="text-xs text-gray-400">L'email ne peut pas être modifié</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Téléphone
          </Label>
          {isEditing ? (
            <Input
              value={editForm.phone || ''}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              placeholder="Votre numéro"
              data-testid="profile-phone-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.phone || '-'}</p>
          )}
        </div>

        {/* Address */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Adresse
          </Label>
          {isEditing ? (
            <Input
              value={editForm.address || ''}
              onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              placeholder="Votre adresse"
              data-testid="profile-address-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.address || '-'}</p>
          )}
        </div>

        {/* City */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Ville
          </Label>
          {isEditing ? (
            <Input
              value={editForm.city || ''}
              onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              placeholder="Votre ville"
              data-testid="profile-city-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.city || '-'}</p>
          )}
        </div>

        {/* Postal Code */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Code postal
          </Label>
          {isEditing ? (
            <Input
              value={editForm.postal_code || ''}
              onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
              placeholder="Code postal"
              data-testid="profile-postal-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.postal_code || '-'}</p>
          )}
        </div>
      </div>
    </div>
  );
};


export const VetProfileEditor = ({ token, onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/veterinarians/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditForm(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Erreur lors du chargement du profil');
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/veterinarians/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          phone: editForm.phone,
          clinic_name: editForm.clinic_name,
          clinic_address: editForm.clinic_address,
          city: editForm.city,
          postal_code: editForm.postal_code,
          bio: editForm.bio,
          consultation_fee: editForm.consultation_fee,
          accepts_emergencies: editForm.accepts_emergencies
        })
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setIsEditing(false);
        toast.success('Profil mis à jour avec succès');
        if (onUpdate) onUpdate(updatedProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erreur de connexion');
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-8 text-gray-500">
        <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Profil non trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <User className="w-5 h-5 text-green-600" />
          Mon Profil Vétérinaire
        </h2>
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            size="sm"
            data-testid="edit-vet-profile-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setIsEditing(false);
                setEditForm(profile);
              }}
              variant="outline"
              size="sm"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
              className="bg-green-500 hover:bg-green-600"
              data-testid="save-vet-profile-btn"
            >
              {isSaving ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <User className="w-4 h-4" />
            Nom complet
          </Label>
          {isEditing ? (
            <Input
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              placeholder="Dr. Nom Prénom"
              data-testid="vet-profile-name-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.name || '-'}</p>
          )}
        </div>

        {/* Email (non-editable) */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </Label>
          <p className="text-gray-800 font-medium py-2">{profile.email || '-'}</p>
          {isEditing && (
            <p className="text-xs text-gray-400">L'email ne peut pas être modifié</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Téléphone
          </Label>
          {isEditing ? (
            <Input
              value={editForm.phone || ''}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              placeholder="Numéro de téléphone"
              data-testid="vet-profile-phone-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.phone || '-'}</p>
          )}
        </div>

        {/* Clinic Name */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Nom de la clinique
          </Label>
          {isEditing ? (
            <Input
              value={editForm.clinic_name || ''}
              onChange={(e) => setEditForm({ ...editForm, clinic_name: e.target.value })}
              placeholder="Nom de votre clinique"
              data-testid="vet-profile-clinic-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.clinic_name || '-'}</p>
          )}
        </div>

        {/* Clinic Address */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Adresse de la clinique
          </Label>
          {isEditing ? (
            <Input
              value={editForm.clinic_address || ''}
              onChange={(e) => setEditForm({ ...editForm, clinic_address: e.target.value })}
              placeholder="Adresse complète"
              data-testid="vet-profile-address-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.clinic_address || '-'}</p>
          )}
        </div>

        {/* City */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Ville
          </Label>
          {isEditing ? (
            <Input
              value={editForm.city || ''}
              onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              placeholder="Ville"
              data-testid="vet-profile-city-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.city || '-'}</p>
          )}
        </div>

        {/* Postal Code */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Code postal
          </Label>
          {isEditing ? (
            <Input
              value={editForm.postal_code || ''}
              onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
              placeholder="Code postal"
              data-testid="vet-profile-postal-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.postal_code || '-'}</p>
          )}
        </div>

        {/* Consultation Fee */}
        <div className="space-y-2">
          <Label className="text-gray-600 flex items-center gap-2">
            Tarif consultation (€)
          </Label>
          {isEditing ? (
            <Input
              type="number"
              value={editForm.consultation_fee || ''}
              onChange={(e) => setEditForm({ ...editForm, consultation_fee: parseFloat(e.target.value) || 0 })}
              placeholder="50"
              data-testid="vet-profile-fee-input"
            />
          ) : (
            <p className="text-gray-800 font-medium py-2">{profile.consultation_fee ? `${profile.consultation_fee}€` : '-'}</p>
          )}
        </div>
      </div>

      {/* Bio */}
      <div className="space-y-2">
        <Label className="text-gray-600">Présentation</Label>
        {isEditing ? (
          <textarea
            value={editForm.bio || ''}
            onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
            placeholder="Présentez-vous en quelques lignes..."
            className="w-full min-h-[100px] px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="vet-profile-bio-input"
          />
        ) : (
          <p className="text-gray-800 py-2">{profile.bio || '-'}</p>
        )}
      </div>

      {/* Subscription Info (non-editable) */}
      <div className="bg-gray-50 rounded-lg p-4 mt-6">
        <h3 className="font-medium text-gray-700 mb-2">Informations d'abonnement</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Forfait actuel:</span>
            <span className="ml-2 font-medium text-green-600 capitalize">{profile.subscription_plan || 'Essentiel'}</span>
          </div>
          <div>
            <span className="text-gray-500">Statut:</span>
            <span className={`ml-2 font-medium ${profile.subscription_status === 'active' ? 'text-green-600' : 'text-orange-500'}`}>
              {profile.subscription_status === 'active' ? 'Actif' : profile.subscription_status || '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientProfileEditor;
